import { DropdownMenuItem, DropdownMenuSection } from '../../component/dropdown-menu/dropdown-menu.component';
import { Product, ProductLine } from '../../model/product/product.model';
import { THEMES } from '../product/product.data';
import { DarkLightSettings } from './theme.data';

// Product lines (by key) surfaced as sub-layers inside the single Themes
// column. Single-appearance themes only, collections like "All In One" are
// dual (Light & Dark) so they can't drive an automatic mode. More lines (or
// more top-level categories) can be added later without changing the layout:
// they just add rows / sub-headers to the scrollable Themes column.
const THEME_LINE_KEYS = [ 'nature', 'cities' ];

function themeItem( product: Product, currentProductKey: string | null ): DropdownMenuItem {
  return {
    value: product.key,
    label: product.name,
    logo: product.logo,
    // the theme's five identity colours under its name (owner 2026-10-13)
    colors: ( product.compactPalette || [] ).slice( 0, 5 ),
    selected: currentProductKey === product.key,
  };
}

/**
 * Builds the Appearance dropdown: one "Themes" box led by the Default row
 * (Auto, Light, Dark of the Codigrate look, three columns), then the Light
 * and Dark columns side by side, each split into Nature and Cities. Selecting a
 * theme applies its colors and flips the site to the theme's appearance.
 *
 * `currentMode` is the active dark/light setting; `currentProductKey` is the
 * selected product theme's key (null when on the default palette).
 */
export function buildAppearanceSections(
  currentMode: string,
  currentProductKey: string | null ): DropdownMenuSection[] {

  // The modes belong to the Codigrate default look (a theme carries its own), so they lead the box as the Default
  // rows over the Light and Dark themes (owner 2026-10-16).
  const onDefault = !currentProductKey;
  // five circles under each, as under a theme (owner 2026-10-16): the Codigrate look's own colours in that mode,
  // its surface first, then the two accents and the text. Auto's circles that change with the mode are cut
  // diagonally, half light and half dark; the accents stay whole.
  const accents = [ '#00BAFF', '#00FFFF' ];
  const lightColors = [ '#EFF1F4', ...accents, '#545454', '#2B2B2B' ];
  const darkColors = [ '#2E2E2E', ...accents, '#CFCFCF', '#F0F0F0' ];
  const autoColors = lightColors.map( ( light, i ) =>
    light === darkColors[ i ] ? light : `linear-gradient(135deg, ${ light } 50%, ${ darkColors[ i ] } 50%)` );
  // Auto on its own full-width row, Light | Dark under it as wide as the theme columns (owner 2026-10-16)
  const autoColumns: DropdownMenuItem[][] = [
    [ { value: DarkLightSettings.auto, variant: 'both', label: 'Auto', colors: autoColors, selected: onDefault && currentMode === DarkLightSettings.auto } ],
  ];
  const lightDarkColumns: DropdownMenuItem[][] = [
    [ { value: DarkLightSettings.light, variant: 'light', label: 'Light', colors: lightColors, selected: onDefault && currentMode === DarkLightSettings.light } ],
    [ { value: DarkLightSettings.dark, variant: 'dark', label: 'Dark', colors: darkColors, selected: onDefault && currentMode === DarkLightSettings.dark } ],
  ];

  const lines = THEME_LINE_KEYS
    .map( key => THEMES.find( line => line.key === key ) )
    .filter( ( line ): line is ProductLine => !!line );

  // a row carries only the theme's name, its group labels say the rest (owner 2026-10-12)
  const isLight = ( product: Product ) => !!product.appearance && product.appearance.startsWith( 'Light' );

  // Light and Dark sit side by side in the one Themes box (owner 2026-10-16), each with its Nature and its Cities
  // themes under a smaller label of their own; the original order is kept within each group.
  const appearanceColumns: DropdownMenuItem[][] = [ { name: 'Light', light: true }, { name: 'Dark', light: false } ].map( group => {
    const column: DropdownMenuItem[] = [ { value: group.name.toLowerCase(), label: group.name, header: true } ];
    lines.forEach( line => {
      const products = line.products.filter( product => isLight( product ) === group.light );
      if ( products.length === 0 ) return;
      column.push( { value: `${ group.name.toLowerCase() }-${ line.key }`, label: line.name, header: true, minorHeader: true } );
      products.forEach( product => column.push( themeItem( product, currentProductKey ) ) );
    } );
    return column;
  } );

  const themesSection: DropdownMenuSection = {
    title: 'Themes',
    description: 'Preview our themes live',
    items: [],
    rows: [
      { header: 'Default', columns: autoColumns },
      { columns: lightDarkColumns },
      { columns: appearanceColumns },
    ],
  };

  return [ themesSection ];
}
